import React from 'react'
import { Link } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import Box1 from '../assets/img/animated-box/animated-box-1.png'
import Box2 from '../assets/img/animated-box/animated-box-2.png'
import Box3 from '../assets/img/animated-box/animated-box-3.png'
import Box4 from '../assets/img/animated-box/animated-box-4.png'
import Box5 from '../assets/img/animated-box/animated-box-5.png'
import Box6 from '../assets/img/animated-box/animated-box-6.png'
import Box7 from '../assets/img/animated-box/animated-box-7.png'
import Colors from '../assets/styles/Colors'
import { useTranslation } from 'react-i18next'
import Responsive from '../assets/config/Responsive'
import RightLineIcon from '../assets/icons/JSX/RightLineIcon'

const changeImages = keyframes`
  0% { background-image: url(${Box1}) }
  14.28% { background-image: url(${Box2})}
  28.56% { background-image: url(${Box3})}
  42.84% { background-image: url(${Box4})}
  57.12% { background-image: url(${Box5})}
  71.4% { background-image: url(${Box6})}
  85.68% { background-image: url(${Box7})}
  100% { background-image: url(${Box1})}
`

const ServiceCard: React.FC<{
  itemTitle: string
  itemBody: string
  itemExcerpt?: string
  itemType: string
  minPrice?: number | string
  hideMinPrice?: boolean
  img: string
  currency?: string
  style?: React.CSSProperties
  bottomText?: string
  arrow?: boolean
  showButton?: boolean
  disabled?: boolean
  onClick?: () => void
  boxAnimation?: boolean
  to?: string
}> = ({
  itemTitle,
  itemBody,
  itemExcerpt,
  itemType,
  minPrice,
  hideMinPrice = false,
  img,
  currency,
  style,
  bottomText,
  arrow = true,
  showButton = true,
  disabled = false,
  onClick,
  boxAnimation = false,
  to = '',
}) => {
  //
  const { t } = useTranslation()
  //
  return (
    <Wrapper style={style} $disabled={disabled} onClick={onClick} className="service-card">
      {boxAnimation && <AnimationBox />}

      {hideMinPrice ? (
        <br />
      ) : (
        <Price>
          from
          <span>
            {minPrice} {currency === 'usd' ? '$' : currency === 'eur' ? '€' : currency}/mo
          </span>
        </Price>
      )}

      <ImageStyle src={img} alt="card image" />

      <InfoBlock>
        <h3>{itemTitle}</h3> <p>{itemBody}</p>
      </InfoBlock>

      {showButton ? (
        <LinkStyle to={to}>
          <span>{bottomText ? bottomText : t('buyIt')}</span> {arrow ? <RightLineIcon /> : null}
        </LinkStyle>
      ) : null}
    </Wrapper>
  )
}

// styles
const AnimationBox = styled.div`
  opacity: 0;
  position: absolute;
  top: 10px;
  right: 20px;
  transition: all 300ms ease;

  width: 48px;
  height: 48px;
  background-size: contain;
  background-repeat: no-repeat;
`
const ImageStyle = styled.img`
  user-select: none;
  pointer-events: none;
  margin-bottom: 45px;
  height: 80px;
  width: 160px;
  object-fit: contain;

  ${Responsive.mobile} {
    margin-bottom: 30px;
  }
`
const InfoBlock = styled.div`
  padding-right: 40px;
  padding-left: 40px;
  user-select: none;
  pointer-events: none;
  /* h3 title */
  h3 {
    font-size: 18px;
    color: ${Colors.Black};
    text-align: center;
    text-transform: capitalize;
    margin-bottom: 28px;
  }

  /* p descirption */
  p {
    font-size: 14px;
    line-height: 22px;
    color: ${Colors.SilverChalice};
    text-align: center;
    margin-bottom: 45px;
  }

  ${Responsive.mobile} {
    padding-right: 25px;
    padding-left: 25px;
    /* h3 title */
    h3 {
      margin-bottom: 15px;
    }

    /* p descirption */
    p {
      margin-bottom: 27px;
    }
  }
`
const Wrapper = styled.div<{ $disabled: boolean }>`
  text-decoration: none;
  background-color: ${Colors.white};
  width: 100%;
  height: 100%;
  border-radius: 8px;
  padding-top: 26px;
  padding-bottom: 65px;
  display: flex;
  flex-direction: column;
  align-items: center;
  user-select: none;
  cursor: pointer;

  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  ${ImageStyle} {
    opacity: ${({ $disabled }) => ($disabled ? '0.7' : '1')};
  }

  ${InfoBlock} {
    opacity: ${({ $disabled }) => ($disabled ? '0.7' : '1')};
  }

  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);

  :hover {
    ${AnimationBox} {
      opacity: 1;
      animation: ${changeImages} 600ms infinite;
    }
  }

  /* responsive */
  ${Responsive.tablet} {
    width: 350px;
  }
  ${Responsive.mobile} {
    width: 100%;
    padding-top: 20px;
    padding-bottom: 45px;
  }
`
const Price = styled.div`
  font-size: 14px;
  color: ${Colors.SilverChalice};
  margin-left: auto;
  margin-right: 26px;
  margin-bottom: 36px;
  /* month price */
  span {
    display: inline-block;
    font-size: 16px;
    color: ${Colors.primaryRed};
    background-color: ${Colors.lightRed};
    padding: 14px 17px;
    border-radius: 30px;
    margin-left: 13px;
  }

  ${Responsive.mobile} {
    font-size: 12px;
    margin-right: 20px;
    margin-bottom: 10px;

    /* month price */
    span {
      font-size: 12px;
      padding: 8px 11px;
    }
  }
`
const LinkStyle = styled(Link)`
  font-size: 14px;
  color: ${Colors.Black};
  text-decoration: none;
  transition: all 100ms ease-in;
  margin-top: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  /* icons */
  svg {
    width: 16px;
    height: 16px;
    margin-left: 8px;
  }

  span {
    line-height: 16px;
  }

  /* hover */
  &:hover {
    font-weight: 600;
    transition: all 100ms ease-in;
    color: ${Colors.Black};
  }
`

export default ServiceCard
